/* ================================================================
   THE LUX BARBER LOUNGE — "The Engraving"
   Motion story: the page engraves itself as you scroll.
   Hairlines draw, type presses in, foil catches light once.
   No-JS = fully visible page (all initial states set here, not CSS).
   ================================================================ */

// ---------- Booking: single source of truth (swaps to GHL later) ----------
const BOOKING_URL = "https://getsquire.com/discover/barbershop/the-lux-barber-lounge-sacramento";
document.querySelectorAll("[data-book]").forEach(a => {
  a.href = BOOKING_URL;
  a.rel = "noopener";
});

// ---------- Year ----------
const yearEl = document.getElementById("year");
if (yearEl) yearEl.textContent = new Date().getFullYear();

// ---------- Nav scrolled state ----------
const nav = document.getElementById("nav");
const onScroll = () => nav.classList.toggle("is-scrolled", window.scrollY > 40);
onScroll();
window.addEventListener("scroll", onScroll, { passive: true });

// ---------- Motion ----------
const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

if (!reduce && window.gsap) {
  gsap.registerPlugin(ScrollTrigger);
  const EASE = "power3.out";

  /* ----- Hero load: the line draws, the room opens from the line ----- */
  const typeCascade = (tl, at, ruleOrigin) => {
    tl.from(".hero__eyebrow", { opacity: 0, y: 14, duration: 0.7 }, at)
      .from(".hero__the", { opacity: 0, y: 24, duration: 0.8 }, at + 0.12)
      .from(".hero__lux", { opacity: 0, y: 60, duration: 1.1 }, at + 0.2)
      .from(".hero__rule", { scaleX: 0, transformOrigin: ruleOrigin, duration: 0.9 }, at + 0.5)
      .from(".hero__rule .dia", { opacity: 0, rotate: 90, duration: 0.5 }, at + 0.8)
      .from(".hero__sub", { opacity: 0, y: 18, duration: 0.9 }, at + 0.65)
      .from(".hero__script", { opacity: 0, duration: 0.8 }, at + 0.85)
      .from(".hero__cta", { opacity: 0, y: 16, duration: 0.7 }, at + 1.0)
      .from(".hero__specimen", { opacity: 0, duration: 0.9 }, at + 1.15)
      .from(".nav", { opacity: 0, y: -12, duration: 0.8 }, at + 1.05)
      .add(() => document.querySelector(".hero__lux--ink").classList.add("is-sheened"), at + 1.5);
    return tl;
  };

  const mm = gsap.matchMedia();
  mm.add("(min-width: 900px)", () => {
    // seam draws center-out, then both panels part from the line
    const tl = gsap.timeline({ defaults: { ease: EASE } });
    tl.fromTo(".hero__seam", { scaleY: 0, transformOrigin: "50% 0%" }, { scaleY: 1, duration: 0.7, ease: "power2.inOut" })
      .fromTo(".hero__panel--clear",
        { clipPath: "inset(0% 100% 0% 0%)" },
        { clipPath: "inset(0% 0% 0% 0%)", duration: 1.15, ease: "power2.inOut", clearProps: "clipPath" }, 0.55);
    typeCascade(tl, 1.3, "0% 50%");
  });
  mm.add("(max-width: 899px)", () => {
    // one hazed world opens from a center line, which then dissolves
    const tl = gsap.timeline({ defaults: { ease: EASE } });
    tl.fromTo(".hero__seam", { scaleY: 0, opacity: 1, transformOrigin: "50% 0%" }, { scaleY: 1, opacity: 1, duration: 0.6, ease: "power2.inOut" })
      .fromTo(".hero__panel--haze",
        { clipPath: "inset(0% 50% 0% 50%)" },
        { clipPath: "inset(0% 0% 0% 0%)", duration: 1.0, ease: "power2.inOut", clearProps: "clipPath" }, 0.45)
      .to(".hero__seam", { opacity: 0, duration: 0.6 }, 1.2)
      .from(".hero__arch", { opacity: 0, scale: 0.96, duration: 1.2, ease: "power2.out" }, 0.9);
    typeCascade(tl, 1.15, "50% 50%");
  });

  /* ----- Shared reveal helper: eyebrow → title → content ----- */
  const reveal = (sectionSel, items, opts = {}) => {
    const section = document.querySelector(sectionSel);
    if (!section) return;
    gsap.from(items.map(i => section.querySelectorAll(i)), {
      scrollTrigger: { trigger: section, start: opts.start || "top 72%" },
      opacity: 0, y: 26, duration: 0.85, ease: EASE, stagger: 0.12,
    });
  };

  /* ----- Proof: rules draw, numerals count ----- */
  gsap.from(".proof__cell", {
    scrollTrigger: { trigger: ".proof", start: "top 78%" },
    opacity: 0, y: 22, duration: 0.8, ease: EASE, stagger: 0.1,
  });
  gsap.set(".proof__num em", { opacity: 0 });
  document.querySelectorAll(".proof__num[data-count]").forEach(el => {
    const target = parseFloat(el.dataset.count);
    const decimals = String(el.dataset.count).includes(".") ? 1 : 0;
    const suffix = el.querySelector("em");
    const obj = { v: 0 };
    gsap.to(obj, {
      v: target, duration: 1.6, ease: "power2.out",
      scrollTrigger: { trigger: el, start: "top 82%" },
      onUpdate() {
        el.childNodes[0].nodeValue = obj.v.toFixed(decimals);
      },
      onComplete() {
        el.childNodes[0].nodeValue = target.toFixed(decimals);
        if (suffix) suffix.style.opacity = 1;
      },
    });
  });

  /* ----- Section titles: pressed in from behind the rule (clip rise) ----- */
  document.querySelectorAll(".section-title").forEach(t => {
    gsap.from(t, {
      scrollTrigger: { trigger: t, start: "top 82%" },
      clipPath: "inset(100% 0 0 0)", y: 26, duration: 1, ease: EASE,
    });
  });

  /* ----- Section reveals (supporting content only — titles handled above) ----- */
  reveal(".lounge", [".eyebrow", ".lead", ".body"]);
  reveal(".services", [".eyebrow", ".menu__row", ".menu__foot"]);
  reveal(".standard", [".eyebrow"]);
  reveal(".ritual", [".eyebrow", ".ritual__foot"]);
  reveal(".voices", [".eyebrow", ".voices__foot"]);
  reveal(".corner", [".eyebrow", ".ledger__row", ".corner__quiet"]);
  reveal(".endcard", [".endcard__script", ".endcard__title", ".btn-plate", ".endcard__spec"], { start: "top 70%" });

  /* ----- Plates etch: frame first, contents after ----- */
  document.querySelectorAll(".plate").forEach(plate => {
    const tl = gsap.timeline({
      scrollTrigger: { trigger: plate, start: "top 82%" },
      defaults: { ease: EASE },
    });
    tl.from(plate, { opacity: 0, scale: 0.97, duration: 0.9 });
    const well = plate.querySelector(".plate__well");
    if (well) tl.from(well, { opacity: 0, duration: 0.7 }, 0.45);
  });

  /* ----- Work strip: editorial stagger ----- */
  reveal(".work", [".eyebrow", ".section-title"]);
  gsap.from(".work__fig", {
    scrollTrigger: { trigger: ".work__strip", start: "top 80%" },
    opacity: 0, y: 30, duration: 0.9, ease: EASE, stagger: 0.15,
  });

  /* ----- Tenets stagger with their rules ----- */
  gsap.from(".tenet", {
    scrollTrigger: { trigger: ".tenets", start: "top 80%" },
    opacity: 0, y: 30, duration: 0.9, ease: EASE, stagger: 0.16,
  });

  /* ----- Ritual: the hairline engraves itself, nodes set into it ----- */
  {
    const track = document.querySelector(".ritual__track");
    if (track) {
      const vertical = () => window.matchMedia("(max-width: 640px)").matches;
      const tl = gsap.timeline({
        scrollTrigger: { trigger: track, start: "top 78%" },
        defaults: { ease: EASE },
      });
      tl.from(".ritual__line", {
        scaleX: () => (vertical() ? 1 : 0),
        scaleY: () => (vertical() ? 0 : 1),
        duration: 1.1, ease: "power2.inOut",
      })
        .from(".rite__node", { opacity: 0, rotate: 90, duration: 0.5, stagger: 0.14 }, 0.35)
        .from(".rite__num", { opacity: 0, y: 10, duration: 0.5, stagger: 0.14 }, 0.45)
        .from(".rite__name", { opacity: 0, y: 14, duration: 0.6, stagger: 0.14 }, 0.5)
        .from(".rite__body", { opacity: 0, y: 14, duration: 0.6, stagger: 0.14 }, 0.6);
    }
  }

  /* ----- Plaques etch like the plates: settle into the wall ----- */
  gsap.from(".plaque", {
    scrollTrigger: { trigger: ".voices__wall", start: "top 80%" },
    opacity: 0, scale: 0.975, duration: 0.9, ease: EASE, stagger: 0.14,
  });

  /* ----- Watermark drift (the one parallax) ----- */
  gsap.to(".standard__watermark", {
    scrollTrigger: { trigger: ".standard", start: "top bottom", end: "bottom top", scrub: 1.2 },
    yPercent: -14, ease: "none",
  });

  /* ----- Corner map draws itself ----- */
  const mapLines = document.querySelectorAll(".map__grid line, .map__lot");
  mapLines.forEach(line => {
    const len = line.getTotalLength ? line.getTotalLength() : 400;
    line.style.strokeDasharray = line.classList.contains("map__lot") ? "4 4" : len;
    if (!line.classList.contains("map__lot")) line.style.strokeDashoffset = len;
  });
  gsap.to(".map__grid line", {
    scrollTrigger: { trigger: ".corner__map", start: "top 78%" },
    strokeDashoffset: 0, duration: 1.4, ease: "power2.inOut", stagger: 0.08,
  });
  gsap.from(".map__pin, .map__you, .map__lot, .map__lot-label, .map__labels text", {
    scrollTrigger: { trigger: ".corner__map", start: "top 78%" },
    opacity: 0, duration: 0.7, ease: EASE, stagger: 0.05, delay: 0.9,
  });

  /* ----- Sticky book bar: after hero, retire at endcard ----- */
  const bookbar = document.getElementById("bookbar");
  const barLink = bookbar.querySelector("[data-book]");
  const setBar = on => {
    bookbar.classList.toggle("is-visible", on);
    bookbar.setAttribute("aria-hidden", String(!on));
    barLink.tabIndex = on ? 0 : -1;
  };
  ScrollTrigger.create({
    start: () => window.innerHeight * 0.9,
    end: () => ScrollTrigger.maxScroll(window) - window.innerHeight * 1.2,
    onToggle: self => setBar(self.isActive),
  });
} else {
  // Reduced motion or no GSAP: page is fully visible; book bar shows
  // statically on mobile (CSS handles position; desktop hides it ≥900px).
  const bookbar = document.getElementById("bookbar");
  if (bookbar) {
    bookbar.classList.add("is-visible");
    bookbar.setAttribute("aria-hidden", "false");
    const link = bookbar.querySelector("[data-book]");
    if (link) link.tabIndex = 0;
  }
}
